import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { setApartmentPricing } from '@/api/generated/sdk.gen';
import type { ApartmentPricing as ApartmentPricingDTO, PricingStrategy, SetApartmentPricingRequest } from '@/api/generated/types.gen';
import { apartmentPricingQuery, pricingStrategiesQuery } from '@/lib/pricing';
import { messageFromError } from '@/lib/api';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, type SelectOption } from '@/components/ui/select';
import { formatMoney } from '@/lib/format';

// The apartment API does not surface a per-apartment currency (one currency per
// Channex property, not exposed). The calendar defaults the same way; revisit if
// multi-currency apartments ever ship.
const CURRENCY = 'EUR';

// minorToMajorStr renders an int64 minor amount as a "70.00"-style major-unit
// string for the editable form.
function minorToMajorStr(minor: number): string {
  return (minor / 100).toFixed(2);
}

// majorStrToMinor parses a major-unit string into minor units. Returns
// undefined on empty / non-finite / negative so the caller flags a validation
// error rather than sending garbage.
function majorStrToMinor(v: string): number | undefined {
  const t = v.trim();
  if (t === '') return undefined;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100);
}

/**
 * ApartmentPricing — the Pricing section on the apartment detail page. Reads the
 * apartment's anchors (base/min/max) + assigned strategy and lets an operator
 * edit them or set the apartment to unmanaged (manual-only). An apartment with
 * no settings row yet shows a "configure" prompt (the query maps 404 → null).
 */
export function ApartmentPricing({
  apartmentID,
  canWrite,
}: {
  apartmentID: string;
  canWrite: boolean;
}) {
  const pricingQ = useQuery(apartmentPricingQuery(apartmentID));
  const strategiesQ = useQuery(pricingStrategiesQuery);
  const [editing, setEditing] = useState(false);

  const pricing = pricingQ.data ?? null;
  const strategies = strategiesQ.data ?? [];
  const configured = pricing !== null;
  // The strategies list is enabled-only. Until it has actually loaded, an
  // assigned id simply "isn't here yet" — don't mistake that for a deleted
  // strategy. Both the read warning and the edit-form option labelling key off
  // this.
  const strategiesLoaded = strategiesQ.isSuccess;

  return (
    <section className="mt-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Pricing</h2>
        {configured && !editing && canWrite && (
          <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>

      {pricingQ.isLoading ? (
        <p className="text-sm text-muted">Loading pricing…</p>
      ) : pricingQ.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {messageFromError(pricingQ.error)}
        </p>
      ) : editing ? (
        <PricingEditForm
          apartmentID={apartmentID}
          pricing={pricing}
          strategies={strategies}
          strategiesLoaded={strategiesLoaded}
          strategiesError={strategiesQ.isError}
          onCancel={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      ) : !configured ? (
        <div>
          <p className="text-sm text-muted">
            Pricing is not configured for this apartment yet. Set a base rate and a min–max range,
            then optionally assign a strategy to let the engine manage rates.
          </p>
          {canWrite && (
            <Button type="button" variant="primary" size="sm" className="mt-4" onClick={() => setEditing(true)}>
              Configure pricing
            </Button>
          )}
        </div>
      ) : (
        <PricingRead pricing={pricing} strategies={strategies} strategiesLoaded={strategiesLoaded} />
      )}
    </section>
  );
}

function PricingRead({
  pricing,
  strategies,
  strategiesLoaded,
}: {
  pricing: ApartmentPricingDTO;
  strategies: PricingStrategy[];
  strategiesLoaded: boolean;
}) {
  const assignedID = pricing.pricing_strategy_id;
  const strat = assignedID ? strategies.find((s) => s.id === assignedID) : undefined;
  // Only call an assigned strategy "missing" once the (enabled-only) list has
  // actually loaded — otherwise a slow/failed strategies fetch reads as if the
  // strategy were disabled, which is wrong corrective advice.
  const orphaned = !!assignedID && !strat && strategiesLoaded;

  return (
    <>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-4">
        <Field label="Base rate" value={formatMoney(pricing.base_rate_minor, CURRENCY)} />
        <Field label="Min price" value={formatMoney(pricing.min_price_minor, CURRENCY)} />
        <Field label="Max price" value={formatMoney(pricing.max_price_minor, CURRENCY)} />
        <Field
          label="Strategy"
          value={
            assignedID ? (
              strat ? (
                <Badge variant="gold">{strat.name}</Badge>
              ) : (
                <span className="font-mono text-xs" title={assignedID}>
                  {assignedID.slice(0, 8)}…
                </span>
              )
            ) : (
              <Badge variant="neutral">Unmanaged</Badge>
            )
          }
        />
      </dl>
      {orphaned && (
        <p className="mt-3 text-xs text-muted">
          Assigned strategy not found — it may have been disabled. Reassign or set to unmanaged.
        </p>
      )}
    </>
  );
}

function PricingEditForm({
  apartmentID,
  pricing,
  strategies,
  strategiesLoaded,
  strategiesError,
  onCancel,
  onSaved,
}: {
  apartmentID: string;
  pricing: ApartmentPricingDTO | null;
  strategies: PricingStrategy[];
  strategiesLoaded: boolean;
  strategiesError: boolean;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [base, setBase] = useState(pricing ? minorToMajorStr(pricing.base_rate_minor) : '');
  const [min, setMin] = useState(pricing ? minorToMajorStr(pricing.min_price_minor) : '');
  const [max, setMax] = useState(pricing ? minorToMajorStr(pricing.max_price_minor) : '');
  const [strategyId, setStrategyId] = useState(pricing?.pricing_strategy_id ?? '');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const baseMinor = majorStrToMinor(base);
      const minMinor = majorStrToMinor(min);
      const maxMinor = majorStrToMinor(max);
      if (baseMinor === undefined) throw new Error('Base rate must be a non-negative number.');
      if (minMinor === undefined) throw new Error('Min price must be a non-negative number.');
      if (maxMinor === undefined) throw new Error('Max price must be a non-negative number.');
      if (minMinor > maxMinor) throw new Error('Min price must not exceed max price.');
      if (baseMinor < minMinor || baseMinor > maxMinor) {
        throw new Error('Base rate must be within the min–max range.');
      }

      const body: SetApartmentPricingRequest = {
        base_rate_minor: baseMinor,
        min_price_minor: minMinor,
        max_price_minor: maxMinor,
      };
      // Unmanaged ⇒ omit pricing_strategy_id. The PUT is a full replace, so an
      // absent strategy clears it to NULL (manual-only) server-side.
      if (strategyId !== '') body.pricing_strategy_id = strategyId;

      return setApartmentPricing({ throwOnError: true, path: { apartmentId: apartmentID }, body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apartment-pricing', apartmentID] });
      toast({ kind: 'success', title: 'Pricing updated' });
      onSaved();
    },
    onError: (err) => setError(messageFromError(err)),
  });

  const strategyOptions: SelectOption[] = [
    { value: '', label: 'Unmanaged — manual only' },
    ...strategies.map((s) => ({ value: s.id, label: s.name })),
  ];
  // Guarantee the currently-assigned strategy is always representable in the
  // control, even when it's absent from the enabled-only list (disabled after
  // assignment) or the list hasn't loaded. Without this the <select> can't show
  // `strategyId`, so a no-touch Save would silently re-persist a value the
  // operator never sees.
  if (strategyId !== '' && !strategies.some((s) => s.id === strategyId)) {
    strategyOptions.push({
      value: strategyId,
      label: strategiesLoaded ? 'Currently assigned (unavailable — disabled)' : 'Currently assigned…',
    });
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        mut.mutate();
      }}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Input
          label={`Base rate (${CURRENCY})`}
          type="number"
          step="0.01"
          min={0}
          value={base}
          onChange={(e) => setBase(e.target.value)}
          required
        />
        <Input
          label={`Min price (${CURRENCY})`}
          type="number"
          step="0.01"
          min={0}
          value={min}
          onChange={(e) => setMin(e.target.value)}
          required
        />
        <Input
          label={`Max price (${CURRENCY})`}
          type="number"
          step="0.01"
          min={0}
          value={max}
          onChange={(e) => setMax(e.target.value)}
          required
        />
      </div>

      <div className="mt-4 max-w-sm">
        <Select
          label="Strategy"
          options={strategyOptions}
          value={strategyId}
          onChange={(e) => setStrategyId(e.target.value)}
        />
        <p className="mt-1.5 text-xs text-muted">
          Unmanaged apartments are never touched by the pricing engine — rates stay whatever you set
          by hand.
        </p>
        {strategiesError && (
          <p className="mt-1.5 text-xs text-destructive">
            Couldn’t load strategies — you can still keep the current one or set the apartment to
            unmanaged.
          </p>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center gap-2">
        <Button type="submit" variant="primary" loading={mut.isPending} disabled={mut.isPending}>
          Save
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={mut.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-[0.06em] text-muted">{label}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}
